// TableSettings.js
import React, { useState } from "react";
import TableSettingModal from "./TableSettingModal";
import apiService from "../services/apiService";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import "./styles/PropertiesComponent.css";
import CommonButton from "../elments/CommonButton";
import Loader from "./Loader";

const PropertiesComponent = ({
  selectedVariable,
  setdataGroup,
  dataGroup,
  selectedKey,
  setSelectedVariable,
  projectId,nets, setNets
}) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [activeTab, setActiveTab] = useState("properties");
  const [netLabel, setNetLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiValidationLoading, setAiValidationLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  // All possible boolean properties with default values
  const allBooleanProperties = {
    removeTab: false,
    mean: false,
    median: false,
    removeIndividual: false,
    summaryNet: false,
    grid: false,
    
    reverseGrid: false,
    addNets: false,
    saveToExcel: false,
    downloadDVExcel: false,
    SPSSSyntax: false,
    conceptTables: false,
    reverseLocation: false,
    dataCuts: false,
    SE: false,
    STD: false,
    rank: false,
    rankalphabetical: false,
    rankCol: false,
    allstats: false,
    showNetswithinLabels: false,
  };

  // All possible text properties with default values
  const allTextProperties = {
    combo: "",
    flttb: "",
    stacked: "",
    nets: "",
    type: selectedVariable?.type || "",
    table: selectedVariable?.table || "freq",
    Qtitle: selectedVariable?.Qtitle || "",
    Logic: selectedVariable?.Logic || ""
  };

  if (!selectedVariable) {
    return (
      <div className="variable-properties">
        <h2>No variable selected</h2>
        <p>Please select a variable from the list to view its properties.</p>
      </div>
    );
  }

  // Helper to get property value (exists? use it, otherwise use default)
  const getPropertyValue = (property, defaultValue = false) => {
    return selectedVariable.hasOwnProperty(property) ? selectedVariable[property] : defaultValue;
  };

  // Helper to set property (appends to selectedVariable if doesn't exist)
  const setPropertyValue = (property, value) => {
    setSelectedVariable((prev) => ({ ...prev, [property]: value }));
  };

  const handleConvertTo3D = () => {
    if (!selectedVariable || !selectedVariable.type?.includes("2DGrid")) return;

    const rowsInput = prompt("Enter number of rows (required):");
    const colsInput = prompt("Enter number of columns (required):");
    
    if (rowsInput === null || colsInput === null) return;
    
    const numRows = parseInt(rowsInput.trim());
    const numCols = parseInt(colsInput.trim());
    
    if (isNaN(numRows) || isNaN(numCols) || numRows <= 0 || numCols <= 0) {
      toast.warning("Please enter valid positive numbers for rows and columns.");
      return;
    }

    const rowMap = new Map();
    const colMap = new Map();
    const uniqueRows = new Set();
    const uniqueCols = new Set();

    selectedVariable.Rows?.forEach((row) => {
      const match = row.index?.match(/^(\d+)c(\d+)$/);
      if (match) {
        uniqueRows.add(match[1]);
        uniqueCols.add(match[2]);
      }
    });

    if (uniqueRows.size !== numRows) {
      toast.warning(`Cannot convert: Expected ${numRows} rows but found ${uniqueRows.size}.`);
      return;
    }

    if (uniqueCols.size !== numCols) {
      toast.warning(`Cannot convert: Expected ${numCols} columns but found ${uniqueCols.size}.`);
      return;
    }

    const rowTitles = new Map();
    const colTitles = new Map();
    
    selectedVariable.Rows?.forEach((row) => {
      const match = row.index?.match(/^(\d+)c(\d+)$/);
      if (match) {
        const r = match[1];
        const c = match[2];
        const parts = row.title?.split(" - ");
        if (parts?.length >= 2) {
          const statement = parts[0].trim();
          const brand = parts[1].trim();
          if (!rowTitles.has(r)) rowTitles.set(r, statement);
          if (!colTitles.has(c)) colTitles.set(c, brand);
        } else {
          if (!rowTitles.has(r)) rowTitles.set(r, "");
          if (!colTitles.has(c)) colTitles.set(c, "");
        }
      }
    });

    uniqueRows.forEach(r => {
      const title = rowTitles.get(r) || "";
      const newRow = { index: r, title };
      const rowWithExclusive = selectedVariable.Rows?.find(row => {
        const match = row.index?.match(/^(\d+)c(\d+)$/);
        return match && match[1] === r && row.exclusive;
      });
      if (rowWithExclusive) newRow.exclusive = true;
      rowMap.set(r, newRow);
    });

    uniqueCols.forEach(c => {
      const title = colTitles.get(c) || "";
      colMap.set(c, { index: c, title });
    });

    const newRows = Array.from(rowMap.values()).sort((a, b) => {
      const ai = a.index === "99" ? 100 : parseInt(a.index);
      const bi = b.index === "99" ? 100 : parseInt(b.index);
      return ai - bi;
    });

    const newCols = Array.from(colMap.values()).sort(
      (a, b) => parseInt(a.index) - parseInt(b.index)
    );

    const newVarName = selectedVariable.varName?.map((vn) => {
      const match = vn.row?.match(/^(\d+)c(\d+)$/);
      if (match) {
        return { ...vn, row: match[1], col: match[2] };
      }
      return vn;
    });

    setSelectedVariable((prev) => ({
      ...prev,
      type: prev.type?.replace("2D", "3D"),
      Rows: newRows,
      Cols: newCols,
      varName: newVarName,
      grid: true,
      reverseGrid: true,
      valueLabels: { "0.0": "No", "1.0": "Yes" },
      values: [
        { index: 0, title: "No" },
        { index: 1, title: "Yes" },
      ],
    }));
  };

  const handleSwapRowsAndCols = async () => {
    if (!selectedVariable?.Rows || !selectedVariable?.Cols) {
      toast.warning("Cannot swap: Missing rows or columns data");
      return;
    }

    const isGridType = selectedVariable.type && 
      (selectedVariable.type.includes("2DGrid") || 
       selectedVariable.type.includes("3DGrid") ||
       selectedVariable.type.includes("Grid"));

    if (!isGridType) {
      toast.warning("Cannot swap: Only grid-type variables support row/column swapping");
      return;
    }

    const confirmSwap = await confirm({
      title: "Swap Rows and Columns",
      message: `Are you sure you want to swap ${selectedVariable.Rows.length} rows with ${selectedVariable.Cols.length} columns?`,
      confirmText: "Swap",
    });
    
    if (!confirmSwap) return;

    const oldRows = JSON.parse(JSON.stringify(selectedVariable.Rows || []));
    const oldCols = JSON.parse(JSON.stringify(selectedVariable.Cols || []));

    const newRows = oldCols.map(col => ({
      index: col.index,
      title: col.title,
      exclusive: col.exclusive || false,
      ...Object.fromEntries(
        Object.entries(col).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    const newCols = oldRows.map(row => ({
      index: row.index,
      title: row.title,
      ...Object.fromEntries(
        Object.entries(row).filter(([key]) => !['index', 'title', 'exclusive'].includes(key))
      )
    }));

    setSelectedVariable(prev => ({
      ...prev,
      Rows: newRows,
      Cols: newCols,
      ...(prev.rowCount && { rowCount: prev.colCount }),
      ...(prev.colCount && { colCount: prev.rowCount })
    }));

    toast.success(`Swapped: ${oldRows.length} rows ↔ ${oldCols.length} cols → now ${newRows.length} rows, ${newCols.length} cols`);
  };

  const createNet = (label, rowIndices) => {
    const newNet = { label, indices: rowIndices };
    setNets((prevNets) => {
      const updatedNets = [...prevNets, newNet];
      addNet(updatedNets);
      return updatedNets;
    });
  };

  const addNet = (updatedNets) => {
    setSelectedVariable((prev) => ({ ...prev, Nets: updatedNets }));
  };

  const saveChanges = async () => {
    if (!selectedVariable) return;

    setLoading(true);
    
    try {
      const updatedDataGroup = { ...dataGroup, [selectedKey]: selectedVariable };
      setdataGroup((prev) => ({ ...prev, [selectedKey]: selectedVariable }));
      setNets([]);

      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      toast.success("Data saved successfully");
    } catch (error) {
      toast.error("Error saving changes: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowsChange = (updatedRows) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Rows: updatedRows }));
    }
  };

  const handleColsChange = (updatedCols) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Cols: updatedCols }));
    }
  };

  const handlestatementsChange = (updatedStatements) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Statements: updatedStatements }));
    }
  };

  const handlevaluesChange = (updatedValues) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, values: updatedValues }));
    }
  };

  const handlevalueLabelsChange = (updatedvalueLabels) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, valueLabels: updatedvalueLabels }));
    }
  };

  const handleNetPropertyChange = (e, property) => {
    if (!selectedVariable) return;

    const newValue = e.target.value;
    
    const processNetValue = (value) => {
      if (!value || typeof value !== 'string') return [];
      
      const netDefinitions = value.split(';').map(part => part.trim()).filter(part => part !== '');
      const nets = [];
      
      netDefinitions.forEach(definition => {
        const parts = definition.split('=').map(part => part.trim());
        
        if (parts.length >= 2) {
          const label = parts[0];
          const indicesPart = parts[1];
          const indices = processIndicesString(indicesPart);
          nets.push({ label, indices });
        } else if (parts.length === 1) {
          const indices = processIndicesString(parts[0]);
          nets.push({ label: parts[0], indices: indices.length > 0 ? indices : [] });
        }
      });
      
      return nets;
    };

    const processIndicesString = (str) => {
      if (!str || typeof str !== 'string') return [];
      
      const result = [];
      const parts = str.split(',').map(part => part.trim());
      
      parts.forEach(part => {
        if (part.includes('-')) {
          const rangeParts = part.split('-').map(p => p.trim());
          if (rangeParts.length === 2) {
            const start = parseInt(rangeParts[0], 10);
            const end = parseInt(rangeParts[1], 10);
            if (!isNaN(start) && !isNaN(end)) {
              for (let i = start; i <= end; i++) result.push(i);
            }
          }
        } else {
          const num = parseInt(part, 10);
          if (!isNaN(num)) result.push(num);
        }
      });
      
      return [...new Set(result)].sort((a, b) => a - b);
    };

    const processedNets = processNetValue(newValue);
    setSelectedVariable((prev) => ({ ...prev, [property]: newValue, Nets: processedNets }));
  };

  const handlePropertyChange = (e, property) => {
    if (!selectedVariable) return;
    const newValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSelectedVariable((prev) => ({ ...prev, [property]: newValue }));
  };

  const renderTabContent = () => {
    if (!selectedVariable) return <div>No variable data available</div>;

    switch (activeTab) {
      case "properties":
        return (
          <div className="properties-container">

            {/* ── Question Title banner ── */}
            {selectedVariable?.Qtitle && (
              <div className="prop-qtitle-banner">
                <span className="prop-qtitle-label">Q</span>
                <span className="prop-qtitle-text">{selectedVariable.Qtitle}</span>
              </div>
            )}

            {/* ── Boolean toggles ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">⚙</span>
                <span className="prop-section-title">Settings</span>
              </div>
              <div className="prop-toggle-grid">
                {Object.entries(allBooleanProperties).map(([property, defaultValue]) => {
                  const isOn = getPropertyValue(property, defaultValue);
                  return (
                    <label
                      key={property}
                      className={`prop-toggle-card ${isOn ? "prop-toggle-card--on" : ""}`}
                      title={property}
                    >
                      <span className="prop-toggle-name">{property}</span>
                      <div className={`prop-toggle-track ${isOn ? "prop-toggle-track--on" : ""}`}>
                        <div className="prop-toggle-thumb" />
                      </div>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handlePropertyChange(e, property)}
                        style={{ display: "none" }}
                      />
                    </label>
                  );
                })}
              </div>
            </div>

            {/* ── Text fields ── */}
            <div className="prop-section">
              <div className="prop-section-header">
                <span className="prop-section-icon">✎</span>
                <span className="prop-section-title">Configuration</span>
              </div>
              <div className="prop-text-grid">
                {[
                  { key: "type",    label: "Type",    placeholder: "" },
                  { key: "table",   label: "Table",   placeholder: "freq" },
                  { key: "Qtitle",  label: "Q Title", placeholder: "Question title..." },
                  { key: "Logic",   label: "Logic",   placeholder: "" },
                  { key: "combo",   label: "Combo",   placeholder: "S3 or S4, S3 and S4;S3 and S4" },
                  { key: "flttb",   label: "Flttb",   placeholder: "S3, S4=1-4 etc" },
                  { key: "stacked", label: "Stacked", placeholder: "1,3,4;1,6,9" },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} className="prop-text-field">
                    <label className="prop-text-label">{label}</label>
                    <input
                      type="text"
                      className="prop-text-input"
                      value={getPropertyValue(key, "")}
                      placeholder={placeholder}
                      onChange={(e) => handlePropertyChange(e, key)}
                    />
                  </div>
                ))}

                {/* nets uses special handler */}
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Nets (text definition)</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("nets", "")}
                    placeholder="NET1=1-5;NET2=6-10;11-15"
                    onChange={(e) => handleNetPropertyChange(e, "nets")}
                  />
                </div>
                                {/* nets uses special handler */}
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">New Key</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("newkey", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handleNetPropertyChange(e, "newkey")}
                  />
                </div>
                <div className="prop-text-field prop-text-field--wide">
                  <label className="prop-text-label">Weight Variable</label>
                  <input
                    type="text"
                    className="prop-text-input"
                    value={getPropertyValue("weightvar", "")}
                    placeholder="Q1,Q2 etc"
                    onChange={(e) => handleNetPropertyChange(e, "weightvar")}
                  />
                </div>                
              </div>
            </div>
          </div>
        );

      case "rows":
        return selectedVariable.Rows?.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Rows Management ({selectedVariable.Rows.length} rows)</h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.Rows}
                onClose={() => {}}
                onRowsChange={handleRowsChange}
                typeofproperty={"rows"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No rows data available</div>
        );

      case "cols":
        return selectedVariable.Cols?.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Columns Management ({selectedVariable.Cols.length} columns)</h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.Cols}
                onClose={() => {}}
                onRowsChange={handleColsChange}
                typeofproperty={"cols"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No columns data available</div>
        );

      case "values":
        return selectedVariable.values?.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Values Management ({selectedVariable.values.length} values)</h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.values}
                onClose={() => {}}
                onRowsChange={handlevaluesChange}
                typeofproperty={"values"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No values data available</div>
        );
        
      case "statements":
        return selectedVariable.Statements?.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Statements Management ({selectedVariable.Statements.length} statements)</h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.Statements}
                onClose={() => {}}
                onRowsChange={handlestatementsChange}
                typeofproperty={"statements"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
  
          </div>
        ) : (
          <div className="no-data-message">No statements data available</div>
        );

      case "valueLabels":
        return selectedVariable.valueLabels && Object.keys(selectedVariable.valueLabels).length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Value Labels Management ({Object.keys(selectedVariable.valueLabels).length} labels)</h3>
            </div>
            <div className="modal-body">
              {(() => {
                const valueLabelsArray = Object.entries(selectedVariable.valueLabels).map(([key, value]) => ({
                  index: key,
                  title: value,
                }));
                return (
                  <TableSettingModal
                    isOpen={true}
                    rows={valueLabelsArray}
                    onClose={() => {}}
                    onRowsChange={(updatedRows) => {
                      const updatedValueLabels = {};
                      updatedRows.forEach((row) => {
                        updatedValueLabels[row.index] = row.title;
                      });
                      handlevalueLabelsChange(updatedValueLabels);
                    }}
                    typeofproperty={"valueLabels"}
                    createNet={createNet}
                    netLabel={netLabel}
                    setNetLabel={setNetLabel}
                  />
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="no-data-message">No value labels data available</div>
        );

      case "nets":
        return selectedVariable?.Nets?.length > 0 ? (
          <div className="nets-container">
            <div className="nets-header-row">
              <span className="nets-count-badge">{selectedVariable.Nets.length} Net{selectedVariable.Nets.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="nets-list">
              {selectedVariable.Nets.map((net, netIdx) => {
                // Resolve index → title from Rows if available
                const rowsMap = {};
                (selectedVariable.Rows || []).forEach(r => { rowsMap[String(r.index)] = r.title; });
return (
  <div key={netIdx} className="net-card">
    <div className="net-card-header">
      <div className="net-card-index">#{netIdx + 1}</div>
      <div className="net-card-label-wrap">
        <input
          className="net-card-label-input"
          value={net.label}
          onChange={(e) => {
            const updated = selectedVariable.Nets.map((n, i) =>
              i === netIdx ? { ...n, label: e.target.value } : n
            );
            setSelectedVariable(prev => ({ ...prev, Nets: updated }));
          }}
        />
      </div>
      <button
        className="net-card-delete"
        title="Remove this net"
        onClick={() => {
          const updated = selectedVariable.Nets.filter((_, i) => i !== netIdx);
          setSelectedVariable(prev => ({ ...prev, Nets: updated }));
          setNets(updated);
        }}
      >✕</button>
    </div>
    <div className="net-card-indices">
      {/* Check if indices is None/null/undefined OR if label is "NPS" (case insensitive) */}
      {(net.indices === null || net.indices === undefined || net.label?.toLowerCase() === "nps") ? (
        // Show all indices from 0 to 10
        [...Array(11).keys()].map((idx) => (
          <span key={idx} className="net-index-chip">
            <span className="net-index-num">{idx}</span>
            {rowsMap[String(idx)] && (
              <span className="net-index-title">{rowsMap[String(idx)]}</span>
            )}
          </span>
        ))
      ) : (
        // Show the specified indices
        net.indices.map((idx, iIdx) => (
          <span key={iIdx} className="net-index-chip">
            <span className="net-index-num">{idx}</span>
            {rowsMap[String(idx)] && (
              <span className="net-index-title">{rowsMap[String(idx)]}</span>
            )}
          </span>
        ))
      )}
    </div>
  </div>
);
              })}
            </div>
          </div>
        ) : (
          <div className="no-data-message">No nets defined for this variable</div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  const handleAIValidation = async () => {
    if (!selectedVariable) {
      toast.warning("No variable properties to validate");
      return;
    }

    setAiValidationLoading(true);
    setAiSuggestions(null);

    try {
      const { data } = await apiService.post("/validate-with-ai", {
        variableData: selectedVariable,
        variableName: selectedKey,
      });

      if (data.success) {
        setAiSuggestions(data.suggestions);
      } else {
        toast.error("AI validation failed: " + data.message);
      }
    } catch (error) {
      toast.error("Error during AI validation: " + error.message);
    } finally {
      setAiValidationLoading(false);
    }
  };

  const applySuggestion = (suggestionType, suggestion) => {
    if (suggestionType === "text_correction" && suggestion.recommendation) {
      toast.info(`Applied text correction: ${suggestion.recommendation}`);
    }
    if (suggestionType === "sorting" && suggestion.recommendation) {
      toast.info(`Applied sorting suggestion: ${suggestion.recommendation}`);
    }
  };

  const handleSwitchBetween = (from, to) => {
    const fromItems = selectedVariable[from] || [];
    const toItems = selectedVariable[to] || [];
    const updatedVariable = { ...selectedVariable, [from]: toItems, [to]: fromItems };
    setSelectedVariable(updatedVariable);
    setdataGroup((prev) => ({ ...prev, [selectedKey]: updatedVariable }));
  };

  const handleSplitRows = async () => {
    if (!selectedVariable?.Rows?.length) {
      toast.warning("No rows to split.");
      return;
    }

    const confirmed = await confirm({
      title: "Split Rows into Individual Questions",
      message: `This will split "${selectedKey}" into ${selectedVariable.Rows.length} separate SingleResponse questions. The original question will be removed. Proceed?`,
      confirmText: "Split",
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      const { Rows, grid, ...baseProps } = selectedVariable;
      const newQuestions = {};

      Rows.forEach((row) => {
        const newKey = `${selectedKey}_${row.index}`;
        const matchingVarName = (selectedVariable.varName || []).filter(
          (vn) => String(vn.row) === String(row.index)
        );

        newQuestions[newKey] = {
          ...baseProps,
          Qtitle: row.title,
          type: "SingleResponse",
          varName: matchingVarName,
        };
      });

      const updatedDataGroup = { ...dataGroup };
      delete updatedDataGroup[selectedKey];
      Object.assign(updatedDataGroup, newQuestions);

      setdataGroup(updatedDataGroup);
      await apiService.post("/save_all_data_list", { projectId, dataGroup: updatedDataGroup });
      toast.success(`Split into ${Object.keys(newQuestions).length} questions successfully!`);
      setSelectedVariable(null);
    } catch (error) {
      toast.error("Error splitting rows: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const hasRows = selectedVariable?.Rows?.length > 0;
  const hasCols = selectedVariable?.Cols?.length > 0;
  const hasStatements = selectedVariable?.Statements?.length > 0;
  const hasValues = selectedVariable?.values?.length > 0;
  const hasValueLabels = selectedVariable?.valueLabels && Object.keys(selectedVariable.valueLabels).length > 0;
  const hasNets = selectedVariable?.Nets?.length > 0;

  return (
    <div className="variable-properties">
      <h2>{selectedKey || "No variable selected"}</h2>

      <div className="tab-navigation">
        <CommonButton
          title="Properties Tab"
          label="Properties"
          onClick={() => setActiveTab("properties")}
          variant="tab"
          isSelected={activeTab === "properties"}
          size="small"
          bold={true}
        />

        {hasRows && (
          <CommonButton
            title={`Rows Tab (${selectedVariable.Rows.length})`}
            label={`Rows (${selectedVariable.Rows.length})`}
            onClick={() => setActiveTab("rows")}
            variant="tab"
            isSelected={activeTab === "rows"}
            size="small"
            bold={true}
          />
        )}

        {hasCols && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Rows"
              onClick={() => handleSwitchBetween("Cols", "Rows")}
              variant="arrow"
            />
            <CommonButton
              title={`Columns Tab (${selectedVariable.Cols.length})`}
              label={`Columns (${selectedVariable.Cols.length})`}
              onClick={() => setActiveTab("cols")}
              variant="tab"
              isSelected={activeTab === "cols"}
              size="small"
              bold={true}
            />
          </>
        )}

        {hasStatements && (
          <>
            <CommonButton
              label="↔"
              title="Switch Cols ↔ Statements"
              onClick={() => handleSwitchBetween("Cols", "Statements")}
              variant="arrow"
            />
            <CommonButton
              title={`Statements Tab (${selectedVariable.Statements.length})`}
              label={`Statements (${selectedVariable.Statements.length})`}
              onClick={() => setActiveTab("statements")}
              variant="tab"
              isSelected={activeTab === "statements"}
              size="small"
              bold={true}
            />
            <CommonButton
              label="⤴"
              title="Switch Rows ↔ Statements"
              onClick={() => handleSwitchBetween("Rows", "Statements")}
              variant="arrow"
            />
          </>
        )}

        {hasValues && (
          <CommonButton
            title={`Values Tab (${selectedVariable.values.length})`}
            label={`Values (${selectedVariable.values.length})`}
            onClick={() => setActiveTab("values")}
            variant="tab"
            isSelected={activeTab === "values"}
            size="small"
            bold={true}
          />
        )}

        {hasValueLabels && (
          <CommonButton
            title={`Value Labels Tab (${Object.keys(selectedVariable.valueLabels).length})`}
            label={`Value Labels (${Object.keys(selectedVariable.valueLabels).length})`}
            onClick={() => setActiveTab("valueLabels")}
            variant="tab"
            isSelected={activeTab === "valueLabels"}
            size="small"
            bold={true}
          />
        )}

        {hasNets && (
          <CommonButton
            title={`Nets Tab (${selectedVariable.Nets.length})`}
            label={`Nets (${selectedVariable.Nets.length})`}
            onClick={() => setActiveTab("nets")}
            variant="tab"
            isSelected={activeTab === "nets"}
            size="small"
            bold={true}
          />
        )}
      </div>

      <div className="ai-validation-section">
        <CommonButton
          title="Validate with AI"
          label={aiValidationLoading ? "Validating..." : "Validate with AI"}
          onClick={handleAIValidation}
          disabled={aiValidationLoading || !selectedVariable}
          variant="purple"
          size="small"
          loading={aiValidationLoading}
        />
      </div>

      {aiSuggestions && (
        <div className="ai-suggestions">
          <h3>AI Suggestions</h3>
          <div className="suggestion-list">
            {aiSuggestions.map((suggestion, index) => (
              <div key={index} className="suggestion-item">
                <p><strong>{suggestion.type}:</strong> {suggestion.message}</p>
                {suggestion.recommendation && (
                  <p><strong>Recommendation:</strong> {suggestion.recommendation}</p>
                )}
                <CommonButton
                  title={`Apply ${suggestion.type} suggestion`}
                  label="Apply Suggestion"
                  onClick={() => applySuggestion(suggestion.type, suggestion)}
                  variant="smallAction"
                  size="small"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedVariable?.tb && (
        <div className="tabulation-syntax-display">
          <h3>Generated Syntax</h3>
          <pre>{selectedVariable.tb}</pre>
        </div>
      )}

      <div className="tab-content">{renderTabContent()}</div>

      {selectedVariable && (
        <div className="action-buttons">
          <CommonButton
            title="Save Changes"
            label={loading ? "Saving..." : "Save Changes"}
            onClick={saveChanges}
            variant="primary"
            size="large"
            loading={loading}
            bold={true}
          />
          
          {selectedVariable.type?.includes("2DGrid") && (
            <CommonButton
              title="Convert 2D to 3D Grid"
              label="Convert to 3D"
              onClick={handleConvertTo3D}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}
          
          {selectedVariable.type &&
            (selectedVariable.type.includes("2DGrid") ||
             selectedVariable.type.includes("3DGrid") ||
             selectedVariable.type.includes("Grid")) && (
            <CommonButton
              title="Swap Rows and Columns"
              label="Swap Rows/Cols"
              onClick={handleSwapRowsAndCols}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {selectedVariable?.Rows?.length > 0 && (
            <CommonButton
              title="Split each row into a separate SingleResponse question"
              label="Split Rows"
              onClick={handleSplitRows}
              variant="secondary"
              size="large"
              bold={true}
            />
          )}

          {loading && <Loader loading={loading} />}
        </div>
      )}
      {JSON.stringify(selectedVariable)}
    </div>
  );
};

export default PropertiesComponent;